import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { Notification } from '@/types';

export function useSSE() {
  const { isAuthenticated } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    const connect = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const token = useAuthStore.getState().accessToken;
      const es = new EventSource(`/api/v1/notifications/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification') {
            addNotification(data.data as Notification);
          }
          // Reset retry count on successful message or heartbeat
          retryCount.current = 0;
        } catch (err) {
          console.error('Failed to parse SSE message', err);
        }
      };

      es.onerror = () => {
        es.close();
        // Exponential backoff reconnect
        const timeout = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
        retryCount.current++;
        reconnectTimeoutRef.current = setTimeout(connect, timeout);
      };
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, addNotification]);
}
